// Pure view-model for the stacked paywall cards. No React, no network.

export const TIER_ACCENT = {
  basic: 'grey',
  fanchat: 'red',
  fanchat_tiri: 'blue',
  active: 'purple',
  elite: 'gold'
};

export const TIER_ICON = {
  basic: 'paywallShield',
  fanchat: 'paywallMessageCircle',
  fanchat_tiri: 'paywallWand',
  active: 'paywallShield',
  elite: 'paywallCrown'
};

const CTA_TIERS = ['fanchat', 'fanchat_tiri', 'active', 'elite'];

// A plan title is translated only while it is still the stock English text for its tier,
// so a title an admin edited is shown as written.
const STOCK_TITLES = {
  basic: 'Free Plan',
  fanchat: 'FanChat Only',
  fanchat_tiri: 'FanChat + TIRI Pack',
  active: 'Active Supporter',
  elite: 'Elite Supporter'
};

const planTitle = (t, plan) =>
  STOCK_TITLES[plan.tier] === plan.title
    ? t(`subscription.paywall.planTitles.${plan.tier}`, { defaultValue: plan.title })
    : plan.title;

// Look a key up and treat an empty result as "not translated".
const lookup = (t, key) => {
  const value = t(key, { defaultValue: '' });
  return value && value !== key ? value : null;
};

// Keys such as '1.25xpoints-earned' contain dots, so i18next cannot look them up reliably.
// The line is built from the plan's own multiplier instead.
const MULTIPLIER_KEY = /^(\d+(?:\.\d+)?)xpoints-earned$/;
const DEFAULT_MULTIPLIER = { basic: 1, active: 1.25, elite: 1.5 };

const formatMultiplier = (value) => (Number.isInteger(value) ? value.toFixed(1) : String(value));

const multiplierLine = (t, plan, feature) => {
  const fromKey = Number(feature.key.match(MULTIPLIER_KEY)[1]);
  const value = plan.pointMultiplier ?? DEFAULT_MULTIPLIER[plan.tier] ?? fromKey;
  return t('subscription.paywall.multiplier', { multiplier: formatMultiplier(value) });
};

const featureLine = (t, plan, feature) =>
  MULTIPLIER_KEY.test(feature.key)
    ? multiplierLine(t, plan, feature)
    : lookup(t, `subscription.features.${plan.tier}.${feature.key}`) ||
      lookup(t, `subscription.features.${feature.key}`) ||
      feature.title;

const intervalAmount = (plan, interval) => {
  const price = (plan.prices || []).find((p) => p.interval === interval);
  if (!price) return null;
  const discounted = price.discount ? price.amount * (1 - price.discount / 100) : price.amount;
  return Math.round(discounted);
};

const BEST_VALUE = { elite: 'yearly' };

const INTERVAL_OPTIONS = [
  { interval: 'monthly', labelKey: 'subscription.paywall.monthly', priceKey: 'subscription.paywall.perMonthShort' },
  { interval: 'yearly', labelKey: 'subscription.paywall.annual', priceKey: 'subscription.paywall.perYearShort' }
];

const intervalOptions = (t, plan, currencySign) =>
  INTERVAL_OPTIONS.flatMap(({ interval, labelKey, priceKey }) => {
    const amount = intervalAmount(plan, interval);
    if (amount == null) return [];
    return [{
      interval,
      label: t(labelKey),
      priceText: t(priceKey, { price: `${currencySign}${amount.toLocaleString('en-US')}` })
    }];
  });

export function buildPlanCards(plans, currentSubscription, { currencySign = '', t }) {
  const sorted = [...(plans || [])].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  const free = sorted.filter((p) => p.tier === 'basic');
  const ordered = [...free, ...sorted.filter((p) => p.tier !== 'basic')];

  const current = currentSubscription
    ? ordered.find((p) => p._id === currentSubscription.planId)
    : null;

  return ordered.map((plan) => {
    const isFree = plan.tier === 'basic';
    const isCurrent = !!current && plan._id === current._id;
    const isLowerThanCurrent = !!current && !isCurrent && (plan.rank ?? 0) < (current.rank ?? 0);
    const amount = intervalAmount(plan, 'monthly');
    const hasCta = !isFree && CTA_TIERS.includes(plan.tier);

    const intervals = isFree ? [] : intervalOptions(t, plan, currencySign);

    // A monthly subscriber can move their own plan to annual billing. Annual cannot go back to
    // monthly, and a plan with no annual price has nowhere to move to.
    const switchableToAnnual =
      isCurrent && !isFree && currentSubscription?.interval === 'monthly' && intervals.some((o) => o.interval === 'yearly');

    // Refunds are not supported, so a yearly subscriber has paid for the year and can only move up
    // to another yearly plan. A higher plan with no annual price is out of reach.
    const hasAnnual = intervals.some((o) => o.interval === 'yearly');
    const mustStayAnnual =
      !!current && !isFree && !isCurrent && !isLowerThanCurrent && currentSubscription?.interval === 'yearly';

    let ctaLabel = null;
    if (switchableToAnnual) ctaLabel = t('subscription.paywall.switchToAnnual');
    else if (isCurrent && !isFree) ctaLabel = t('subscription.paywall.currentPlan');
    else if (hasCta) ctaLabel = t(`subscription.paywall.cta.${plan.tier}`);

    return {
      id: plan._id,
      tier: plan.tier,
      title: planTitle(t, plan),
      subtitle: plan.description || '',
      accent: TIER_ACCENT[plan.tier] || 'grey',
      icon: TIER_ICON[plan.tier] || 'paywallShield',
      isFree,
      isCurrent,
      priceText: isFree
        ? t('subscription.paywall.free')
        : amount == null ? '' : t('subscription.paywall.pricePerMonth', { price: `${currencySign}${amount}` }),
      intervals:
        switchableToAnnual || (mustStayAnnual && hasAnnual)
          ? intervals.map((o) => (o.interval === 'monthly' ? { ...o, disabled: true } : o))
          : intervals,
      defaultInterval: switchableToAnnual || (mustStayAnnual && hasAnnual) ? 'yearly' : 'monthly',
      bestValueInterval: BEST_VALUE[plan.tier] || null,
      bestValueLabel: t('subscription.paywall.bestValue'),
      lines: (plan.features || []).map((f) => featureLine(t, plan, f)),
      ctaLabel,
      ctaDisabled:
        !isFree && !switchableToAnnual && (isCurrent || isLowerThanCurrent || (mustStayAnnual && !hasAnnual))
    };
  });
}
